/**
 * TOP 7 Bundle Selector
 * Per-card add-to-cart via the native Eurus cart contract
 * (xCartHelper.withCartLock -> cart/add.js -> reRenderSections -> mini cart),
 * plus the mobile slider progress bar.
 * Registered on alpine:init so it re-initialises safely after theme-editor reloads.
 */
(() => {
  const register = () => {
    if (!window.Alpine || window.__top7BundleRegistered) return;
    window.__top7BundleRegistered = true;

    Alpine.data('xTop7BundleSelector', (config) => ({
      sectionId: (config && config.sectionId) || '',
      loading: false,
      // Own-scope declarations so these never leak into (or clobber) a parent component's scope.
      root: null, errorEl: null, liveEl: null, cardsEl: null, progressEl: null,

      init() {
        this.root = this.$el;
        this.errorEl = this.root.querySelector('[data-t7b-error]');
        this.liveEl = this.root.querySelector('[data-t7b-live]');
        this.cardsEl = this.root.querySelector('[data-t7b-cards]');
        this.progressEl = this.root.querySelector('[data-t7b-progress]');

        // Event delegation survives theme-editor section re-renders.
        this.root.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-t7b-add]');
          if (btn) return this.addToCart(btn);
          // Anywhere else on a pack card opens the product page with that pack selected.
          const card = e.target.closest('[data-t7b-href]');
          if (card && !e.target.closest('a, button, input, select, label')) this.openCard(card);
        });

        if (this.cardsEl && this.progressEl) {
          this.cardsEl.addEventListener('scroll', () => this.updateProgress(), { passive: true });
          window.addEventListener('resize', () => this.updateProgress(), { passive: true });
          this.updateProgress();
        }
      },

      openCard(card) {
        const href = card.dataset.t7bHref;
        if (!href) return;
        // Already on that product page: scroll up to the buy box and select the pack instead of reloading.
        const box = document.querySelector('.t7pb-inner');
        if (box && window.location.pathname.replace(/\/$/, '') === href.split('?')[0].replace(/\/$/, '')) {
          const qtyInput = document.querySelector('[data-t7pb-qty-input]');
          const qty = parseInt(card.dataset.t7bQty, 10) || 1;
          if (qtyInput) { qtyInput.value = qty; qtyInput.dispatchEvent(new Event('input', { bubbles: true })); }
          box.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
        window.location.href = href;
      },

      updateProgress() {
        if (!this.cardsEl || !this.progressEl) return;
        const max = this.cardsEl.scrollWidth - this.cardsEl.clientWidth;
        const visible = max > 0 ? this.cardsEl.clientWidth / this.cardsEl.scrollWidth : 1;
        const pos = max > 0 ? this.cardsEl.scrollLeft / max : 0;
        const fill = visible + pos * (1 - visible);
        this.progressEl.style.width = Math.min(100, Math.max(8, fill * 100)) + '%';
      },

      showError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message || '';
        this.errorEl.classList.toggle('t7b-error--visible', !!message);
      },

      addToCart(button) {
        if (this.loading) return;
        const variantId = button.dataset.variantId;
        const quantity = parseInt(button.dataset.quantity, 10) || 1;
        if (!variantId) return;

        this.loading = true;
        this.showError('');
        button.setAttribute('disabled', 'disabled');
        button.setAttribute('aria-busy', 'true');

        const items = [{ id: variantId, quantity: quantity }];
        const helper = window.Alpine && Alpine.store('xCartHelper');
        const miniCart = window.Alpine && Alpine.store('xMiniCart');

        const done = () => {
          this.loading = false;
          button.removeAttribute('disabled');
          button.removeAttribute('aria-busy');
        };

        const doFetch = (standardEvent) =>
          fetch((window.Shopify && Shopify.routes ? Shopify.routes.root : '/') + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: items,
              sections: helper ? helper.getSectionsToRender().map((s) => s.id) : undefined
            })
          })
            .then((r) => (helper ? helper.cartJson(r) : r.json()))
            .then((response) => {
              if (response.status == '422') {
                this.showError(response.description || response.message || '');
                document.dispatchEvent(new CustomEvent('eurus:product:add-failed', {
                  detail: { errorMessage: response.description }
                }));
                if (helper) return helper.settleWithCart(standardEvent, helper.userErrorsFrom(response));
                return;
              }
              if (helper && response.sections) helper.reRenderSections(response.sections);
              if (this.liveEl) {
                this.liveEl.textContent = (button.textContent || '').trim() + ' — added to cart';
              }
              document.dispatchEvent(new CustomEvent('eurus:product:added', { detail: { product: response.items || response } }));
              if (miniCart && Alpine.store('xCartNoti') && Alpine.store('xCartNoti').enable) {
                Alpine.store('xCartNoti').setItem(response);
              } else if (miniCart) {
                miniCart.openCart();
                document.dispatchEvent(new CustomEvent('eurus:cart:redirect'));
              }
              document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));
              if (helper) return helper.settleWithCart(standardEvent);
            })
            .catch((error) => {
              if (standardEvent) standardEvent.fail(error);
              this.showError(typeof error === 'string' ? error : (error && error.message) || 'Something went wrong. Please try again.');
              document.dispatchEvent(new CustomEvent('eurus:product:add-failed', { detail: { errorMessage: error } }));
              console.error('TOP7 bundle add error:', error);
            })
            .finally(done);

        if (helper && helper.withCartLock) {
          helper.withCartLock((standardEvent) => doFetch(standardEvent), {
            type: 'shopify:cart:lines-update',
            action: 'add',
            context: 'product',
            lines: helper.addLines ? helper.addLines(items) : undefined
          });
        } else {
          doFetch(null);
        }
      }
    }));
  };

  document.addEventListener('alpine:init', register);
  if (window.Alpine) register();
})();
